import React from "react";
import "./style.scss";
import { Button, Modal } from "antd";
import { formatTime } from "shared/utils/time";
import { CategorySelector } from "../FilterDashboardManagerForm/CategorySelector";
import { ProjectForm } from "features/architecture/projects/components/ProjectForm";
import { createProjectSelectionAdapter } from "features/architecture/projects/model/adapters/createProjectSelectionAdapter";
import { SelectOption } from "components/AntDesign/Select/BaseSelect";

type StopwatchViewProps = {
  seconds: number;
  isRunning: boolean;
  hasPausedSession: boolean;
  isPreviousDaySession: boolean;
  canStart: boolean;
  warning: string | null;
  filteredCategoryOptions: SelectOption<number>[];
  categoriesLoading: boolean;
  onCategorySearchChange: (value: string) => void;
  projectSelection: ReturnType<typeof createProjectSelectionAdapter>;
  primaryAction: {
    label: string;
    onClick: () => void;
    disabled: boolean;
  };
  onSave: () => Promise<void>;
  onReset: () => void;
  isConfirmingPreviousDay: boolean;
  formattedSessionDate: string | null;
  onCloseConfirm: () => void;
  onSavePreviousDay: () => Promise<void>;
  onResetPreviousDay: () => void;
};

const StopwatchDisplay = ({ seconds }: { seconds: number }) => (
  <div className="stopwatch-display">{formatTime(seconds)}</div>
);

export const StopwatchView: React.FC<StopwatchViewProps> = ({
  seconds,
  isRunning,
  hasPausedSession,
  isPreviousDaySession,
  canStart,
  warning,
  filteredCategoryOptions,
  categoriesLoading,
  onCategorySearchChange,
  projectSelection,
  primaryAction,
  onSave,
  onReset,
  isConfirmingPreviousDay,
  formattedSessionDate,
  onCloseConfirm,
  onSavePreviousDay,
  onResetPreviousDay,
}) => {
  return (
    <div className="stopwatch">
      <div className="content">
        <div className="stopwatch-header">
          <h2>Stopwatch</h2>
        </div>

        <StopwatchDisplay seconds={seconds} />

        <div className="form-project">
          <ProjectForm
            disabled={isRunning || isPreviousDaySession}
            selection={projectSelection}
          />

          <CategorySelector
            options={filteredCategoryOptions}
            value={projectSelection.state.category}
            onChange={projectSelection.setCategory}
            onSearch={onCategorySearchChange}
            loading={categoriesLoading}
            disabled={isRunning || isPreviousDaySession}
          />
        </div>

        <div className="stopwatch-buttons">
          <Button
            onClick={primaryAction.onClick}
            type="primary"
            className="stopwatch-button"
            disabled={primaryAction.disabled}
          >
            {primaryAction.label}
          </Button>

          <Button
            disabled={isRunning || !hasPausedSession || !canStart}
            onClick={onSave}
            className="stopwatch-button"
          >
            Save
          </Button>

          <Button
            disabled={isRunning || !hasPausedSession}
            onClick={onReset}
            className="reset-button"
          >
            Reset
          </Button>
        </div>

        {warning && (
          // Render availability feedback as an explicit validation-style alert so it reads as blocking state.
          <div className="field-error stopwatch-warning" role="alert" aria-live="polite">
            {warning}
          </div>
        )}

        <Modal
          open={isConfirmingPreviousDay}
          title={`Save session from ${formattedSessionDate}?`}
          onCancel={onCloseConfirm}
          footer={[
            <Button key="save" onClick={onSavePreviousDay}>
              Save
            </Button>,
            <Button key="reset" danger onClick={onResetPreviousDay}>
              Reset
            </Button>,
          ]}
        >
          <p>
            This paused session was recorded on{" "}
            <strong>{formattedSessionDate}</strong>. It will be saved using
            that original date.
          </p>
        </Modal>
      </div>
    </div>
  );
};
